import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  checkedIcon,
  close,
  dropwdownIcon,
  languageIcon,
  logo,
  logoMobile,
  menus,
  messageQuestion,
  nofitication,
  search,
  syncIcon,
} from "../assets/images/images";
import { logout } from "../services/api/auth.service";
import { useLocation, useNavigate } from "react-router-dom";
import {
  publish,
  subscribe,
  unsubscribe,
} from "../services/loadingServices/loading.service";
import {
  updateCurrentActive,
  updateCurrentCompanyActive,
} from "../services/api/locations.services";
import { updateLanguage } from "../services/api/account.service";
import { loadLocalTranslations, getSupportedLocale } from "../services/localeLoader";
import LanguageContext from "../context/LanguageContext";
import { showToast } from "./ToastProvider";
import { showApiError } from "../services/apiError";
import { syncDataToRedis } from "../services/api/redis.service";
import MenuIcon from "../assets/images/MenuIcon";
import { useResponsive } from "../hook/useResponsive";
import { DEFAULT_ACCESS_CONTEXT } from "../services/accessContext";
import { selectedLocation } from "../services/currentLocation";

// ── search constants ────────────────────────────────────────────────────────

/** Display order for grouped categories in the search dropdown. */
const CATEGORY_ORDER = [
  "Setup",
  "People",
  "Catalogue",
  "Sales",
  "Inventory",
  "Finance",
  "Devices",
  "Reports",
  "Other",
] as const;

/** Title → category. Anything missing falls into "Other". */
const MODULE_CATEGORIES: Record<string, string> = {
  // Setup
  "Company Details": "Setup",
  Locations: "Setup",
  "Floor Plans": "Setup",
  "Sale Points": "Setup",
  Languages: "Setup",
  "Payment Terms": "Setup",
  Tax: "Setup",
  Roles: "Setup",
  Schedules: "Setup",
  "Transaction Month": "Setup",
  Reasons: "Setup",
  // People
  Employees: "People",
  Clients: "People",
  "Device Users": "People",
  // Catalogue
  Items: "Catalogue",
  Attributes: "Catalogue",
  "Product Modifiers": "Catalogue",
  "Order Templates": "Catalogue",
  Promotion: "Catalogue",
  // Sales
  Sales: "Sales",
  "Recent Sales": "Sales",
  "Order Management": "Sales",
  "Order Approval": "Sales",
  // Inventory
  Inventory: "Inventory",
  Transfers: "Inventory",
  Deliveries: "Inventory",
  "Stock Counts": "Inventory",
  "Count Approval": "Inventory",
  Corrections: "Inventory",
  "Supplier Inventory": "Inventory",
  "Supplier Return": "Inventory",
  // Finance
  Purchases: "Finance",
  "Purchase Ledger": "Finance",
  "Sales Ledger": "Finance",
  Assets: "Finance",
  // Devices
  Devices: "Devices",
  "Device Tender": "Devices",
  "Device Health": "Devices",
  "App Version": "Devices",
  // Reports
  "General Report": "Reports",
  "Item Movement": "Reports",
};

/** Shown under "Quick access" when the search is empty. */
const PINNED_MODULES = ["Items", "Recent Sales", "Order Management", "Employees"];

const RECENT_SEARCHES_KEY = "header.search.recent";
const RECENT_SEARCHES_MAX = 5;

function loadRecentTitles(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "string") : [];
  } catch {
    return [];
  }
}

function saveRecentTitles(titles: string[]): void {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(titles));
  } catch {
    /* localStorage may be unavailable (private mode, quota) — fail silent */
  }
}

/**
 * Subsequence matcher — every char of `query` must appear in `text` in order
 * but not necessarily adjacent. Returns the matched indices in `text`, or
 * null if the query can't be matched. Greedy from the left.
 *
 * Enables abbreviation search:
 *   "om"  → "Order Management"     (indices 0, 6)
 *   "rsv" → "Reservations"         (indices 0, 2, 4)
 */
function getMatchIndices(text: string, query: string): number[] | null {
  const t = text.toLowerCase();
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const indices: number[] = [];
  let j = 0;
  for (let i = 0; i < t.length && j < q.length; i++) {
    if (t[i] === q[j]) {
      indices.push(i);
      j++;
    }
  }
  return j === q.length ? indices : null;
}

/**
 * Score a candidate against a query. Higher is better; -1 means no match.
 *
 * Tier 1 — prefix       (text starts with the query)
 * Tier 2 — substring    (query appears contiguously somewhere)
 * Tier 3 — fuzzy        (subsequence; word-boundary hits bonus)
 *
 * Within each tier, shorter text and tighter matches win.
 */
function scoreMatch(text: string, query: string): number {
  const t = text.toLowerCase();
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  if (t.startsWith(q)) return 10000 - t.length;
  const idx = t.indexOf(q);
  if (idx >= 0) return 5000 - idx * 10 - t.length;
  const indices = getMatchIndices(text, query);
  if (!indices) return -1;
  const span = indices[indices.length - 1] - indices[0] + 1;
  let wordBoundaryHits = 0;
  for (const i of indices) {
    if (i === 0 || /[\s\-_]/.test(text[i - 1])) wordBoundaryHits++;
  }
  return 1000 + wordBoundaryHits * 100 - span - t.length;
}

/**
 * Bold the matched substring(s) inside a result row. Prefers contiguous
 * substring (visually obvious) and falls back to per-char highlights from
 * the subsequence matcher.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q.toLowerCase());
  if (idx >= 0) {
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: "#0F7679", fontWeight: 700 }}>
          {text.slice(idx, idx + q.length)}
        </span>
        {text.slice(idx + q.length)}
      </>
    );
  }
  const indices = getMatchIndices(text, query);
  if (!indices) return text;
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const i of indices) {
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <span key={i} style={{ color: "#0F7679", fontWeight: 700 }}>
        {text[i]}
      </span>,
    );
    last = i + 1;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** Small inline `<kbd>`-style badge used in the search input and footer hint. */
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 6px",
        background: "#F4F8F9",
        border: "1px solid #E5ECEC",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        color: "#6A7878",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        letterSpacing: 0.2,
      }}
    >
      {children}
    </span>
  );
}

/** Each language under its own name, so it can be found from any UI language. */
const NATIVE_LANGUAGE_NAMES: Record<string, string> = { en: "English", al: "Shqip", sq: "Shqip" };

const languageLabel = (item: any): string => {
  const native = NATIVE_LANGUAGE_NAMES[String(item?.code ?? "").toLowerCase()];
  if (native) return native;
  const name = String(item?.name ?? "");
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

interface HeaderProps {
  /** Drawer state is owned by DashboardLayout (it also closes the drawer on
      outside click and when SidebarCoreMobile navigates), so the header only
      reflects and toggles it — never keeps its own copy. */
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  userInfor: any;
  listCompany: any;
  listLanguage: any;
  getAccountInfor: (isLoading: boolean) => Promise<void>;
}

const Header = ({
  userInfor,
  listCompany,
  listLanguage,
  getAccountInfor,
  isSidebarOpen,
  onToggleSidebar,
}: HeaderProps) => {
  const location = useLocation();
  const nav = useNavigate();
  const inforRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notiRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isMobile, getWidth } = useResponsive();

  const [searchModules, setSearchModules] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [recentTitles, setRecentTitles] = useState<string[]>(() => loadRecentTitles());
  const [modules, setModules] = useState<any>([
    { id: "", title: "Company Details" },
    { id: "", title: "Locations" },
    { id: "", title: "Floor Plans" },
    { id: "", title: "Sale Points" },
    { id: "", title: "Employees" },
    { id: "", title: "Clients" },
    { id: "", title: "Items" },
    { id: "", title: "Attributes" },
    { id: "", title: "Promotion" },
    { id: "", title: "Inventory" },
    { id: "", title: "Product Modifiers" },
    { id: "", title: "Order Management" },
    { id: "", title: "Transfers" },
    { id: "", title: "Deliveries" },
    { id: "", title: "Stock Counts" },
    { id: "", title: "Count Approval" },
    { id: "", title: "Corrections" },
    { id: "", title: "Order Templates" },
    { id: "", title: "Order Approval" },
    { id: "", title: "Supplier Inventory" },
    { id: "", title: "Supplier Return" },
    { id: "", title: "Devices" },
    { id: "", title: "Device Users" },
    { id: "", title: "Device Tender" },
    { id: "", title: "Device Health" },
    { id: "", title: "App Version" },
    { id: "", title: "Reasons" },
    { id: "", title: "Tax" },
    { id: "", title: "Payment Terms" },
    { id: "", title: "Roles" },
    { id: "", title: "Languages" },
    { id: "", title: "Purchases" },
    { id: "", title: "Sales" },
    { id: "", title: "Assets" },
    { id: "", title: "Purchase Ledger" },
    { id: "", title: "Sales Ledger" },
    { id: "", title: "Recent Sales" },
    { id: "", title: "General Report" },
    { id: "", title: "Item Movement" },
    { id: "", title: "Schedules" },
    { id: "", title: "Transaction Month" },
  ]);

  const { language, setLanguage } = useContext(LanguageContext);

  // Row label only: the English title stays the identity for permissions,
  // routing, recents and categories.
  const moduleLabel = useCallback(
    (title: string): string => (language && language[title]) ?? title,
    [language],
  );
  const [showInfor, setShowInfor] = useState(false);
  const [showNoti, setShowNoti] = useState(false);
  const [isMOdulesDropdownOpen, setIsModulesDropdownOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const userId: string = localStorage.getItem("userId") || "";
  const companyId: string = localStorage.getItem("companyId") || "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsModulesDropdownOpen(false);
      }
      if (
        inforRef.current &&
        !inforRef.current.contains(event.target as Node)
      ) {
        setShowInfor(false);
      }
      if (notiRef.current && !notiRef.current.contains(event.target as Node)) {
        setShowNoti(false);
      }
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMobileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleRolePermission = (data: any) => {
      const permissionMap = new Map<string, boolean>();
      data.detail.forEach((section: any) => {
        section.value.forEach((perm: any) => {
          permissionMap.set(perm.name, perm.value);
        });
      });
      const updatedModules = modules.map((mod: any) => {
        const allowed = permissionMap.has(mod.title)
          ? permissionMap.get(mod.title)
          : false;
        return { ...mod, showRoute: allowed };
      });
      setModules(updatedModules);
    };
    subscribe("modulePermission", handleRolePermission);
    return () => unsubscribe("modulePermission", handleRolePermission);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await logout({
        sessionId: localStorage.getItem("sessionId") || "",
      });
      if (response.data.success) {
        const lang = localStorage.getItem("language");
        localStorage.clear();
        if (lang) {
          localStorage.setItem("language", lang);
        }
        window.location.href = "/";
      }
    } catch (error: any) {
      showApiError(error, { language });
    }
  };

  useEffect(() => {
    const getDefaultLanguage = async (code: string) => {
      try {
        const locale = getSupportedLocale(code);
        const languageFormatted = await loadLocalTranslations(locale);
        localStorage.setItem("language", locale);
        setLanguage(languageFormatted);
        window.languageContext = {
          language: languageFormatted,
          setLanguage: () => { },
        };
        document.dispatchEvent(new CustomEvent("languageLoaded"));
      } catch (error) {
        console.error("Error loading language:", error);
      }
    };
    const pathSegment = location.pathname.split("/")[1];
    const code = pathSegment || localStorage.getItem("language") || "en";
    getDefaultLanguage(code);
  }, []);

  // Every loader writes localStorage before it sets the language, so that is what is on screen;
  // the URL can lag behind it (a link with another locale), so it is only the fallback.
  const currentLocale = getSupportedLocale(
    localStorage.getItem("language") || location.pathname.split("/")[1] || "en",
  );
  // At most one pill is current, and only a language the loader really has.
  const currentLanguageIndex = (listLanguage ?? []).findIndex((v: any) => {
    const code = String(v?.code ?? "").toLowerCase();
    return code in NATIVE_LANGUAGE_NAMES && getSupportedLocale(code) === currentLocale;
  });

  const handleSelectLanguage = async (value: any) => {
    const path: any = location.pathname
      .split("/")
      .filter((v: any) => v !== "" && v.length > 2);
    try {
      await updateLanguage({ languageId: value?.id });
      const code = value?.code ? getSupportedLocale(value.code) : "en";
      const languageFormatted = await loadLocalTranslations(code);
      localStorage.setItem("language", code);
      setLanguage(languageFormatted);
      window.languageContext = {
        language: languageFormatted,
        setLanguage: () => { },
      };
      document.dispatchEvent(new CustomEvent("languageLoaded"));
      nav("/" + code + "/" + path.join("/") + location.search);
    } catch (error) {
      showApiError(error, { language });
    }
  };

  const handleUpdateLocation = async (id: string) => {
    publish("loading", true);
    try {
      await updateCurrentActive({ id, companyId, userId });
      localStorage.setItem("locationId", id);
    } catch (error) {
      showApiError(error, { language });
    } finally {
      await getAccountInfor(true);
    }
  };

  const handleUpdateComopaniesLocation = async (id: string) => {
    publish("loading", true);
    try {
      const response: any = await updateCurrentCompanyActive({
        companyId: id,
        userId,
      });
      const idC: any = response?.data?.find(
        (v: any) => v.isCurrentActive
      )?.companyId;
      localStorage.setItem("companyId", idC);
      window.location.reload();
    } catch (error) {
      showApiError(error, { language });
    } finally {
      await getAccountInfor(true);
    }
  };

  const handleClientSelect = (module: any) => {
    setSearchModules("");
    setIsModulesDropdownOpen(false);
    setHighlightedIndex(0);

    // Persist to recent (most-recent first, dedupe, cap at MAX).
    const next = [
      module.title,
      ...recentTitles.filter((t) => t !== module.title),
    ].slice(0, RECENT_SEARCHES_MAX);
    setRecentTitles(next);
    saveRecentTitles(next);

    publish("changeActiveRedirect", { value: module.title, createNew: false });
  };

  // All modules the user is permitted to see — drives every search surface.
  const allowedModules = useMemo(
    () => modules.filter((v: any) => v?.showRoute),
    [modules],
  );

  /**
   * Sections shown in the dropdown:
   *   • empty query → "Recent" (if any) + "Quick access" (pinned)
   *   • non-empty query → modules grouped by category, in CATEGORY_ORDER
   */
  const visibleSections = useMemo(() => {
    const findByTitle = (title: string) =>
      allowedModules.find((m: any) => m.title === title);

    const q = searchModules.trim().toLowerCase();
    if (!q) {
      const sections: { label: string; items: any[] }[] = [];
      const recentItems = recentTitles
        .map((t) => findByTitle(t))
        .filter(Boolean);
      if (recentItems.length > 0) sections.push({ label: "Recent", items: recentItems });

      const pinnedItems = PINNED_MODULES
        .filter((t) => !recentTitles.includes(t))
        .map((t) => findByTitle(t))
        .filter(Boolean);
      if (pinnedItems.length > 0) sections.push({ label: "Quick access", items: pinnedItems });
      return sections;
    }

    // Score every candidate; keep matches; sort each category by score.
    const scored = allowedModules
      .map((m: any) => ({
        m,
        score: Math.max(scoreMatch(m.title, q), scoreMatch(moduleLabel(m.title), q)),
      }))
      .filter((x: any) => x.score >= 0);

    const grouped = new Map<string, { m: any; score: number }[]>();
    for (const x of scored) {
      const cat = MODULE_CATEGORIES[x.m.title] ?? "Other";
      if (!grouped.has(cat)) grouped.set(cat, []);
      grouped.get(cat)!.push(x);
    }
    for (const arr of grouped.values()) {
      arr.sort((a, b) => b.score - a.score);
    }
    return CATEGORY_ORDER.map((label) => ({
      label,
      items: (grouped.get(label) ?? []).map((x) => x.m),
    })).filter((s) => s.items.length > 0);
  }, [allowedModules, searchModules, recentTitles, moduleLabel]);

  /** Flat list across all visible sections — drives keyboard nav indexing. */
  const flatItems = useMemo(
    () => visibleSections.flatMap((s) => s.items),
    [visibleSections],
  );

  // Reset the keyboard highlight whenever the visible list changes: the query,
  // the permissions behind it and the language of the labels all reorder it.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [flatItems]);

  // Global ⌘K / Ctrl+K to focus the search (desktop affordance; harmless on mobile).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setIsModulesDropdownOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ↑ / ↓ / Enter / Esc on the search input. Works against `flatItems`.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isMOdulesDropdownOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, Math.max(flatItems.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const item = flatItems[highlightedIndex];
      if (item) {
        e.preventDefault();
        handleClientSelect(item);
      }
    } else if (e.key === "Escape") {
      setIsModulesDropdownOpen(false);
      setSearchModules("");
      searchInputRef.current?.blur();
    }
  };

  const clearSearch = () => {
    setSearchModules("");
    setHighlightedIndex(0);
    searchInputRef.current?.focus();
  };

  const clearRecent = () => {
    setRecentTitles([]);
    saveRecentTitles([]);
    setHighlightedIndex(0);
  };

  /** Translation lookup with explicit English fallback. */
  const t = (key: string, fallback?: string): string =>
    (language && language[key]) ?? fallback ?? key;

  // Auto-scroll the highlighted row into view when arrow-keying past the fold.
  const dropdownBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMOdulesDropdownOpen) return;
    const el = dropdownBodyRef.current?.querySelector<HTMLElement>(
      `[data-row-index="${highlightedIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, isMOdulesDropdownOpen]);

  /**
   * Shared dropdown body used by both desktop and mobile blocks.
   * Renders sectioned results with bolded matches, a chevron affordance,
   * an empty-state, and a keyboard-hint footer.
   */
  const renderSearchDropdownBody = (): JSX.Element => {
    if (flatItems.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "16px 12px",
            color: "#6A7878",
            fontSize: 13,
          }}
        >
          {searchModules.trim()
            ? t("No results")
            : t("Start typing to search")}
        </div>
      );
    }

    let runningIndex = -1; // mirrors flatItems indexing across sections
    const hasRecent = !searchModules.trim() && recentTitles.length > 0;

    return (
      <>
        {visibleSections.map((section) => {
          const isRecent = section.label === "Recent";
          return (
            <div key={section.label} style={{ marginBottom: 4 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px 4px",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: "#5E6E75",
                  }}
                >
                  {t(section.label)}
                </span>
                {isRecent && hasRecent && (
                  <button
                    type="button"
                    onClick={clearRecent}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label={t("Clear recent")}
                    title={t("Clear recent")}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#5E6E75",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: "0 4px",
                    }}
                  >
                    {t("Clear recent")}
                  </button>
                )}
              </div>
              {section.items.map((client: any) => {
                runningIndex += 1;
                const myIndex = runningIndex;
                const isHighlighted = myIndex === highlightedIndex;
                return (
                  <div
                    key={`${client.id}-${client.title}-${myIndex}`}
                    data-row-index={myIndex}
                    onMouseEnter={() => setHighlightedIndex(myIndex)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleClientSelect(client)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: isHighlighted ? "#E7F1F1" : "transparent",
                      cursor: "pointer",
                      transition: "background 120ms ease",
                    }}
                    className="text-main text-[14px] font-tafel"
                  >
                    <span
                      style={{
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {highlightMatch(moduleLabel(client.title), searchModules)}
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={isHighlighted ? "#0F7679" : "#C9D7D8"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, transition: "stroke 120ms ease" }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                );
              })}
            </div>
          );
        })}
        {/* Keyboard hint footer — desktop affordance; harmless to ship on
            both because tablets often have keyboards too. */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            padding: "8px 10px 4px",
            marginTop: 4,
            borderTop: "1px solid #E5ECEC",
            fontSize: 11,
            color: "#5E6E75",
          }}
        >
          <span>
            <Kbd>↑↓</Kbd> {t("navigate")}
          </span>
          <span>
            <Kbd>↵</Kbd> {t("select")}
          </span>
          <span>
            <Kbd>esc</Kbd> {t("close")}
          </span>
        </div>
      </>
    );
  };

  const handleShowSidebar = () => {
    onToggleSidebar();
  };

  /** Name shown on the mobile location chip (same source as the sidebar). */
  const activeLocation = selectedLocation(
    userInfor?.locations,
    window.accessContext ?? DEFAULT_ACCESS_CONTEXT
  );
  const activeLocationName =
    activeLocation?.name ||
    activeLocation?.location?.name ||
    t("Head Office", "Head Office");

  const handleSyncData = async () => {
    // Guard: ignore rapid double-clicks while a sync is already in flight.
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const { data } = await syncDataToRedis(companyId);
      // The refresh answers 200 even when some tables could not be rebuilt; say so.
      // The all-companies refresh keys its lists by company id; keep the id with its tables.
      const byCompany = (map?: Record<string, string[]>): string[] =>
        Object.entries(map ?? {}).map(([id, tables]) => `${id} (${tables.join(", ")})`);
      const failed: string[] = [
        ...(data?.failedTables ?? []),
        ...byCompany(data?.failedTablesByCompany),
      ];
      const missing: string[] = [
        ...(data?.missingSnapshots ?? []),
        ...byCompany(data?.missingSnapshotsByCompany),
      ];
      // An enable-list gap is an ops detail (the tables were synced anyway), not a user warning.
      if (data?.requiredTablesNotEnabled?.length) {
        console.warn("Required sync tables missing from the enable-list:", data.requiredTablesNotEnabled);
      }
      if (failed.length || missing.length) {
        const lines = [
          failed.length ? `${t("Tables not refreshed")}: ${failed.join(", ")}` : "",
          missing.length ? `${t("Snapshots missing after refresh")}: ${missing.join(", ")}` : "",
        ].filter(Boolean);
        showToast({
          message: t("Data refresh completed with warnings"),
          desc: `<p class='font-tafel text-[14px] text-main leading-6'>${lines.join("<br/>")}</p>`,
          type: "warning",
          duration: 10000,
        });
      } else {
        showToast({
          message: t("Data refresh successfully completed!"),
          type: "success",
          duration: 3000,
        });
      }
    } catch (error: any) {
      showApiError(error, { language, titleFallback: t("Data Refresh Error. Please Try Again or Contact Support.") });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      <div className="lg:h-[72px] lg:px-3 flex lg:items-center border-b border-b-[#EBF0F0] max-lg:px-4 max-lg:py-3">
        <div className="w-[244px] bg-[#F8F8F8] h-full flex items-center border-r border-r-[#EBF0F0] max-lg:hidden">
          <img src={logo} className="h-[22px] my-auto lg:block hidden" />
        </div>
        <div className="max-lg:flex hidden items-center w-full">
          {/* Menu first, then the brand. -ml-3 lines the glyph up with the page's 16px edge. */}
          <button
            type="button"
            className="cursor-pointer w-11 h-11 -ml-3 flex items-center justify-center shrink-0"
            onClick={handleShowSidebar}
            aria-label={t("Menu")}
            aria-expanded={isSidebarOpen}
            title={t("Menu")}
          >
            <MenuIcon />
          </button>
          <img src={logo} alt="Finex" className="h-[20px] w-auto shrink-0" />
          <div className="flex items-center gap-x-2 ml-auto">
            {/* Location chip — opens the drawer, where the branch switcher is. */}
            <button
              type="button"
              onClick={handleShowSidebar}
              className="flex items-center gap-x-1 h-[30px] max-w-[120px] px-2 border border-[#DBE4E4] rounded-lg"
              aria-label={t("Location")}
              title={activeLocationName}
            >
              <span className="text-[12.5px] font-medium text-[#222525] truncate">
                {activeLocationName}
              </span>
              <svg
                width="13"
                height="13"
                viewBox="0 0 20 20"
                fill="none"
                stroke="#6A7878"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden="true"
              >
                <path d="M4.5 7.5l5.5 5.5 5.5-5.5" />
              </svg>
            </button>
            {/* Sync has its own button in the row; the bell it replaces had no notifications behind it. */}
            <button
              type="button"
              className="min-w-[44px] min-h-[44px] -my-1.5 -ml-1.5 -mr-1 flex items-center justify-center disabled:opacity-60"
              onClick={handleSyncData}
              disabled={isSyncing}
              aria-label={t("Sync data")}
              aria-busy={isSyncing}
              title={t("Sync data")}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-[10px] border border-[#DBE4E4] p-[6px]">
                <img
                  src={syncIcon}
                  alt=""
                  className={`w-full h-full ${isSyncing ? "header-sync-spin" : ""}`}
                />
              </span>
            </button>
            <div className="cursor-pointer relative" ref={mobileDropdownRef}>
              <button
                type="button"
                className="flex items-center justify-center rounded-full min-w-[44px] min-h-[44px] -my-1.5 -mr-1.5 -ml-1"
                onClick={() => setShowMobileDropdown(!showMobileDropdown)}
                aria-haspopup="menu"
                aria-expanded={showMobileDropdown}
                aria-label={language?.["Account"] ?? "Account"}
              >
                {userInfor?.image ? (
                  <span className="block w-8 h-8 rounded-full overflow-hidden">
                    <img
                      src={userInfor?.image}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  </span>
                ) : (
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#0F7679] text-white text-[11px] font-semibold uppercase">
                    {userInfor?.firstName?.slice(0, 1)}
                    {userInfor?.lastName?.slice(0, 1)}
                  </span>
                )}
              </button>
              {showMobileDropdown && (
                <div className="duration-300 z-[200] w-[300px] rounded-lg absolute top-full shadow-md px-2 right-0 bg-white space-y-2 mt-2">
                  <div className="flex items-center gap-x-1 pt-2">
                    {userInfor?.image ? (
                      <div className="w-9 h-9 cursor-pointer rounded-full overflow-hidden">
                        <img
                          src={userInfor?.image}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-[#44b8b5] text-white uppercase">
                        {userInfor?.firstName?.slice(0, 1)}
                        {userInfor?.lastName?.slice(0, 1)}
                      </div>
                    )}
                    <div>
                      <p className="text-lg text-main font-medium">
                        {userInfor?.firstName} {userInfor?.lastName}
                      </p>
                      <p className="text-[16px] text-subtitle font-medium">
                        {userInfor?.emailAddress}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-t-[#e7f2f1] py-2">
                    <button
                      type="button"
                      onClick={() => {
                        nav(`/${currentLocale}/accountInfor`);
                        setShowMobileDropdown(false);
                      }}
                      className="w-full text-left text-base rounded-md cursor-pointer hover:bg-[#E7F1F1] text-[18px] text-subtitle font-bold hover:text-main p-3 duration-300"
                    >
                      {language?.["Account"] ?? "Account"}
                    </button>
                    {/* A labelled choice with the current language marked, not bare words to guess at. */}
                    <div className="px-3 py-2" role="radiogroup" aria-label={t("Language")}>
                      <p className="flex items-center gap-x-2 text-[13px] font-medium text-[#6A7878] mb-2">
                        <img src={languageIcon} className="w-4 h-4" alt="" />
                        {t("Language")}
                      </p>
                      <div className="flex gap-x-2">
                        {listLanguage?.map((v: any, index: number) => {
                          const isCurrent = index === currentLanguageIndex;
                          return (
                            <button
                              key={v?.id ?? v?.code}
                              type="button"
                              role="radio"
                              aria-checked={isCurrent}
                              className={`flex-1 min-w-0 h-11 px-3 rounded-full border text-[15px] font-medium flex items-center justify-center gap-x-1.5 duration-200 ${isCurrent
                                ? "bg-[#0F7679] border-[#0F7679] text-white"
                                : "bg-white border-[#C9D7D8] text-[#222525] active:bg-[#E7F1F1]"
                                }`}
                              onClick={() => {
                                setShowMobileDropdown(false);
                                if (!isCurrent) handleSelectLanguage(v);
                              }}
                            >
                              {isCurrent && (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="shrink-0"
                                  aria-hidden="true"
                                >
                                  <path d="M4 10.5l4 4 8-8.5" />
                                </svg>
                              )}
                              <span className="truncate">{languageLabel(v)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="w-full text-left text-base rounded-md cursor-pointer hover:bg-[#E7F1F1] text-[18px] text-subtitle font-bold hover:text-main p-3 duration-300"
                      onClick={handleLogout}
                    >
                      {language?.["Log out"] ?? "Log out"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className="flex flex-1 items-center justify-between px-6">
            <div className="relative" ref={dropdownRef} style={{ width: 400 }}>
              {/* Search icon — absolutely positioned inside the wrapper so it
                  doesn't accidentally count as "outside the dropdown". */}
              <div
                className="absolute pointer-events-none"
                style={{ top: "50%", left: 14, transform: "translateY(-50%)", width: 18, height: 18 }}
              >
                <img src={search} alt="" style={{ width: "100%", height: "100%" }} />
              </div>

              <input
                ref={searchInputRef}
                type="text"
                value={searchModules}
                placeholder={t("SearchModulesPlaceholder", "Search modules…")}
                onChange={(e) => {
                  setSearchModules(e.target.value);
                  setIsModulesDropdownOpen(true);
                }}
                onFocus={() => setIsModulesDropdownOpen(true)}
                onKeyDown={handleSearchKeyDown}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 56px 0 40px", // left room for search icon, right room for clear / kbd
                  borderRadius: 12,
                  fontWeight: 500, // medium, not semibold — less shouty
                }}
                className="text-[14px] text-main placeholder:font-normal placeholder:text-[#6A7878] outline-none border border-[#C9D7D8] focus:border-[#0F7679] transition-colors duration-200 bg-white"
              />

              {/* Right adornment: clear button when there's text, ⌘K hint otherwise */}
              <div
                className="absolute"
                style={{ top: "50%", right: 8, transform: "translateY(-50%)" }}
              >
                {searchModules ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label={t("Clear search")}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "#6A7878",
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                ) : (
                  <span style={{ pointerEvents: "none" }}>
                    <Kbd>⌘K</Kbd>
                  </span>
                )}
              </div>

              {isMOdulesDropdownOpen && (
                <div
                  ref={dropdownBodyRef}
                  className="header-search-dropdown absolute z-10 mt-2 bg-white border border-[#C9D7D8] rounded-xl overflow-auto scroll-container"
                  style={{
                    width: "100%",
                    maxHeight: 380,
                    padding: 6,
                    boxShadow: "0 8px 28px rgba(5,41,42,0.12)",
                  }}
                >
                  {renderSearchDropdownBody()}
                </div>
              )}
            </div>
            <div className="flex justify-center gap-x-3">
              <button
                type="button"
                className="cursor-pointer w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#E7F1F1] focus:outline-none focus:ring-2 focus:ring-[#0F7679] focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                onClick={handleSyncData}
                disabled={isSyncing}
                aria-label={t("Sync data")}
                aria-busy={isSyncing}
                title={t("Sync data")}
              >
                <img
                  src={syncIcon}
                  alt=""
                  className={isSyncing ? "header-sync-spin" : ""}
                />
              </button>
              <div className="flex items-end gap-x-2 cursor-pointer relative group">
                <div className="cursor-pointer w-10 h-10 flex items-center justify-center">
                  <img src={languageIcon} className="max-lg:hidden" />
                </div>
                <div className="duration-300 right-0 hidden group-hover:block h-0 group-hover:h-fit w-[270px] bg-white rounded-lg absolute top-full shadow-md p-2 z-[100]">
                  {listLanguage?.map((v: any, i: any) => (
                    <div
                      key={i}
                      className="text-[14px] text-subtitle font-tafel leading-[22px] hover:text-main flex items-center justify-between hover:bg-[#E7F1F1] duration-300 px-2 cursor-pointer"
                      onClick={() => handleSelectLanguage(v)}
                    >
                      <p className="py-2 duration-300">{v?.name}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* <div className="relative group cursor-pointer max-lg:hidden">
                <div className="w-10 h-10 flex items-center justify-center rounded-full group-hover:bg-[#b9d5d5] duration-300 p-1">
                  <div className="relative w-full h-full">
                    <img src={nofitication} className="w-full h-full" />
                    <div className="absolute w-[6px] h-[6px] rounded-full bg-[#44b8b5] bottom-full right-[2px] translate-y-[90%] hidden group-hover:block duration-300"></div>
                  </div>
                </div>
                <div className="duration-300 z-[100] hidden group-hover:block h-0 group-hover:h-fit w-[450px] rounded-lg absolute top-full shadow-md px-2 right-0 bg-white space-y-1">
                  <div className="flex items-center justify-between pt-2 ">
                    <p className="text-xl text-black font-bold">
                      Notifications{" "}
                      <span className="text-xl text-subtitle font-bold">
                        (3)
                      </span>
                    </p>
                    <p className="text-sm text-[#21A6AA] font-bold cursor-pointer">
                      Mark all as read
                    </p>
                  </div>
                  <div className="border-t border-t-[#e7f2f1] py-2">
                    <div className="flex items-start justify-between hover:bg-[#E7F1F1] duration-300 p-2 rounded-md">
                      <div className="flex items-center gap-x-2">
                        <div className="w-[6px] h-[6px] rounded-full bg-[#44b8b5]"></div>
                        <div>
                          <p className="text-lg text-main font-medium">
                            Workflow Title Set
                          </p>
                          <p className="text-[16px] text-subtitle font-medium">
                            A short description added for this workflow.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-end gap-x-2">
                        <p className="text-[16px] text-subtitle font-medium">
                          3h
                        </p>
                        <p className="text-[16px] text-subtitle font-medium">
                          ...
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="cursor-pointer relative group h-[40px] w-[68px] max-lg:hidden">
                {userInfor?.image ? (
                  <div className="flex items-center gap-x-1 rounded-full ">
                    <div className="w-[40px] h-[40px] cursor-pointer rounded-full overflow-hidden">
                      <img
                        src={userInfor?.image}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <img src={dropwdownIcon} className="w-6 h-6" />
                  </div>
                ) : (
                  <div className="flex items-center gap-x-1 rounded-full ">
                    <div className="flex items-center justify-center h-[40px] w-[40px] rounded-full bg-[#44b8b5] text-white uppercase">
                      {userInfor?.firstName?.slice(0, 1)}
                      {userInfor?.lastName?.slice(0, 1)}
                    </div>
                    <img src={dropwdownIcon} className="w-6 h-6" />
                  </div>
                )}
                <div className="duration-300 hidden z-[200] group-hover:block h-0 group-hover:h-fit w-[300px] rounded-lg absolute top-full shadow-md px-2 right-0 bg-white space-y-2">
                  <div className="flex items-center gap-x-1 pt-2">
                    {userInfor?.image ? (
                      <div className="w-9 h-9 cursor-pointer rounded-full overflow-hidden">
                        <img
                          src={userInfor?.image}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-[#44b8b5] text-white uppercase">
                        {userInfor?.firstName?.slice(0, 1)}
                        {userInfor?.lastName?.slice(0, 1)}
                      </div>
                    )}
                    <div>
                      <p className="text-lg text-main font-medium">
                        {userInfor?.firstName} {userInfor?.lastName}
                      </p>
                      <p className="text-[16px] text-subtitle font-medium">
                        {userInfor?.emailAddress}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-t-[#e7f2f1] py-2">
                    <div
                      onClick={() => nav(`/${currentLocale}/accountInfor`)}
                      className="w-full text-base rounded-md cursor-pointer hover:bg-[#E7F1F1] text-[18px] text-subtitle font-bold hover:text-main p-3 duration-300"
                    >
                      {language?.["Account"] ?? "Account"}
                    </div>
                    <div
                      className="w-full text-base rounded-md cursor-pointer hover:bg-[#E7F1F1] text-[18px] text-subtitle font-bold hover:text-main p-3 duration-300"
                      onClick={handleLogout}
                    >
                      {language?.["Log out"] ?? "Log out"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isMobile && (
        <div className="flex p-4 items-center justify-center">
          <div className="relative w-full" ref={dropdownRef}>
            {/* Search icon inside the wrapper so a tap on it counts as
                inside the dropdown (won't close it). */}
            <div
              className="absolute pointer-events-none"
              style={{ top: "50%", left: 14, transform: "translateY(-50%)", width: 18, height: 18 }}
            >
              <img src={search} alt="" style={{ width: "100%", height: "100%" }} />
            </div>

            <input
              type="text"
              value={searchModules}
              placeholder={t("SearchModulesPlaceholder", "Search modules…")}
              onChange={(e) => {
                setSearchModules(e.target.value);
                setIsModulesDropdownOpen(true);
              }}
              onFocus={() => setIsModulesDropdownOpen(true)}
              onKeyDown={handleSearchKeyDown}
              style={{
                width: "100%",
                height: 44,
                padding: "0 44px 0 40px",
                borderRadius: 12,
                fontWeight: 500,
              }}
              className="text-[15px] text-main placeholder:font-normal placeholder:text-[#6A7878] outline-none border border-[#C9D7D8] focus:border-[#0F7679] transition-colors duration-200 bg-white"
            />

            {/* Clear (×) button when there's text — replaces the desktop
                ⌘K hint, which has no meaning on a touch device. */}
            {searchModules && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label={t("Clear search")}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: 8,
                  transform: "translateY(-50%)",
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#6A7878",
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}

            {isMOdulesDropdownOpen && (
              <div
                ref={dropdownBodyRef}
                className="header-search-dropdown absolute z-10 w-full mt-2 bg-white border border-[#C9D7D8] rounded-xl overflow-auto scroll-container"
                style={{
                  maxHeight: 380,
                  padding: 6,
                  boxShadow: "0 8px 28px rgba(5,41,42,0.12)",
                }}
              >
                {renderSearchDropdownBody()}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
